import { isPlatformServer } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';

import { LocalStoreService, OnAppInit, WINDOW } from '@frontend/vanilla/core';
import { LaunchDarklyService } from '@frontend/vanilla/features/launch-darkly';
import { LDEvaluationDetail } from 'launchdarkly-js-client-sdk';
import { get } from 'lodash-es';
import { Observable, catchError, forkJoin, map, of, take, timeout } from 'rxjs';

import { LDFeatureFlags, LD_FEATURE_FLAG_STORAGE_KEY, LaunchDarklyFlagTypes } from './flags';

type LaunchDarklyFlagType<T extends LDFeatureFlags> = T extends keyof LaunchDarklyFlagTypes ? LaunchDarklyFlagTypes[T] : never;

interface FeatureFlagOptions<T extends LDFeatureFlags> {
    allowedEmissions: number;
    timeoutInMS: number;
    defaultValue: LaunchDarklyFlagType<T>;
}

@Injectable({ providedIn: 'root' })
export class LaunchDarklyFlagManagerService implements OnAppInit {
    private isServer = isPlatformServer(inject(PLATFORM_ID));
    private localStoreService = inject(LocalStoreService);
    private launchDarklyService = inject(LaunchDarklyService);

    /**
     * After talking with Pushbased and Vanilla there is no better way of accessing the lazy feature configs since every other way is vanilla core internal.
     */
    readonly #window = inject(WINDOW);

    private featureFlagKeys$: Record<string, Observable<string | boolean | undefined>> = {
        [LDFeatureFlags.BetslipMode]: this.launchDarklyService.getFlagOnly$(LDFeatureFlags.BetslipMode).pipe(take(1)),
        [LDFeatureFlags.ReuseSelections]: this.launchDarklyService.getFlagOnly$(LDFeatureFlags.ReuseSelections).pipe(take(1)),
        [LDFeatureFlags.ESoccerInAZMenu]: this.launchDarklyService.getFlagOnly$(LDFeatureFlags.ESoccerInAZMenu).pipe(take(1)),
        [LDFeatureFlags.EnableLiveAlerts]: this.launchDarklyService.getFlagOnly$(LDFeatureFlags.EnableLiveAlerts).pipe(take(1)),
        [LDFeatureFlags.BannerGamesDisplayType]: this.launchDarklyService.getFlagOnly$(LDFeatureFlags.BannerGamesDisplayType).pipe(take(1)),
    };

    onAppInit() {
        if (!this.isServer) {
            forkJoin(this.featureFlagKeys$).subscribe((featureFlags) => {
                this.localStoreService.set(LD_FEATURE_FLAG_STORAGE_KEY, featureFlags);
            });
        }
    }

    getFeatureFlag = (flagName: string) =>
        (this.localStoreService.get(LD_FEATURE_FLAG_STORAGE_KEY) as Record<string, string | boolean | undefined>)?.[flagName];

    activateExperiment$(key: string): Observable<LDEvaluationDetail> {
        return this.launchDarklyService.getFeatureFlagValueWithEvaluationReasons$(key);
    }

    isFlagInContext(flag: LDFeatureFlags) {
        return this.launchDarklyService.client?.allFlags()[flag] !== undefined;
    }

    isFlagInLocalStorage(flag: LDFeatureFlags) {
        return this.getFeatureFlag(flag) !== undefined;
    }

    /**
     * @description A wrapper around Vanilla's LaunchDarklyService that provides type-safe flag values.
     */
    getFeatureFlagValue$<T extends LDFeatureFlags>(featureFlagKey: T, defaultValue: LaunchDarklyFlagType<T>) {
        return this.launchDarklyService
            .getFeatureFlagValue$(featureFlagKey, defaultValue)
            .pipe(map((value) => (typeof value === typeof defaultValue ? (value as LaunchDarklyFlagType<T>) : defaultValue)));
    }

    /**
     * This a wrapper function on the vanilla implementation of the getFeatureFlagValue$ function in order to handle specific cases.
     * If the timing is very important, you can configure your custom timeout and you don't have to wait 5 seconds before timeout.
     * If the LD sdk is not loaded for your label, you can immediately return with default value instead of waiting 5 seconds.
     * You can configure how many flag variations you want to consume. If your experiment would cause a big layout shift it is recommended to use some other strategy.
     * @param featureFlagKey
     * @param allowedEmissions
     * @param timeoutInMS Timeout value we give LD to evaluate our flags.
     * @param defaultValue
     * @returns
     */
    getFeatureFlagValueWithCustomConditions$<T extends LDFeatureFlags>(featureFlagKey: T, options: FeatureFlagOptions<T>) {
        const { allowedEmissions, timeoutInMS, defaultValue } = options;
        return this.isLDFeatureEnabled()
            ? this.getFeatureFlagValue$(featureFlagKey, defaultValue).pipe(
                  take(allowedEmissions),
                  timeout({ first: timeoutInMS }),
                  catchError(() => of(defaultValue)),
              )
            : of(defaultValue);
    }

    private isLDFeatureEnabled() {
        const isFeatureEnabled = get(this.#window, ['clientConfig', 'vnLazyFeatures', 'rules', 'launch-darkly', 'enabled'], false);

        return typeof isFeatureEnabled === 'string' && isFeatureEnabled.toLowerCase() === 'true';
    }
}
